import { CONVERSATION_PRIORITY_ORDER } from 'shared/constants/messages';

export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

export const filterByStatus = (chatStatus, filterStatus) =>
  filterStatus === 'all' ? true : chatStatus === filterStatus;

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

// NOTICE: KAPTION-INTEGRATION
// team-filter-scope.CLIENT.1 — this must accept whatever the server accepted.
//
// The server's group filter can read a group two ways (account.settings
// team_filter_scope): the conversations the TEAM owns, or those plus the ones its
// MEMBERS own. This filter only knew the first, so under the second reading it threw
// away every row the server had just returned — the conversations of a group's people
// carry no team_id. An empty list plus a non-zero server count is exactly the state
// ChatList's pagination pins to page 1, so the disagreement did not render as "no
// conversations", it rendered as an unbounded refetch: 264 identical requests in one
// minute on 2026-08-27 (account 28, team 32).
//
// `teamMemberIds` is supplied by the caller for the group currently being viewed; with
// it empty this behaves exactly as before.
export const filterByTeam = (
  shouldFilter,
  teamId,
  chatTeamId,
  chatAssigneeId,
  teamMemberIds = []
) => {
  if (!teamId) return shouldFilter;

  const isOnTeam = Number(teamId) === chatTeamId;
  const isOwnedByTeamMember =
    !!chatAssigneeId &&
    teamMemberIds.map(Number).includes(Number(chatAssigneeId));

  return (isOnTeam || isOwnedByTeamMember) && shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};
export const filterByUnattended = (
  shouldFilter,
  conversationType,
  firstReplyOn,
  waitingSince
) => {
  return conversationType === 'unattended'
    ? (!firstReplyOn || !!waitingSince) && shouldFilter
    : shouldFilter;
};

export const applyPageFilters = (conversation, filters) => {
  const {
    inboxId,
    status,
    labels = [],
    teamId,
    conversationType,
    // NOTICE: KAPTION-INTEGRATION — team-filter-scope.CLIENT.1
    teamMemberIds = [],
  } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    meta = {},
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
  } = conversation;
  const team = meta.team || {};
  const { id: chatTeamId } = team;
  const chatAssigneeId = meta.assignee?.id;

  let shouldFilter = filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(
    shouldFilter,
    teamId,
    chatTeamId,
    chatAssigneeId,
    teamMemberIds
  );
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    firstReplyOn,
    waitingSince
  );

  return shouldFilter;
};

/**
 * Determines if a conversation is "unassigned" based on the account's assignment consideration mode.
 *
 * @param {Object} conversation - The conversation object
 * @param {string} assignmentConsideration - The assignment consideration mode ('person', 'team', 'both')
 * @returns {boolean} - Whether the conversation is considered unassigned
 */
export const isConversationUnassigned = (
  conversation,
  assignmentConsideration = 'person'
) => {
  const hasNoAssignee = !conversation.meta?.assignee;
  const hasNoTeam = !conversation.meta?.team?.id;

  switch (assignmentConsideration) {
    case 'team':
      return hasNoTeam;
    case 'both':
      return hasNoAssignee && hasNoTeam;
    case 'person':
    default:
      return hasNoAssignee;
  }
};

/**
 * Filters conversations based on user role and permissions
 *
 * @param {Object} conversation - The conversation object to check permissions for
 * @param {string} role - The user's role (administrator, agent, etc.)
 * @param {Array<string>} permissions - List of permission strings the user has
 * @param {number|string} currentUserId - The ID of the current user
 * @param {string} assignmentConsideration - The assignment consideration mode ('person', 'team', 'both')
 * @returns {boolean} - Whether the user has permissions to access this conversation
 */
export const applyRoleFilter = (
  conversation,
  role,
  permissions,
  currentUserId,
  assignmentConsideration = 'person'
) => {
  // the role === "agent" check is typically not correct on it's own
  // the backend handles this by checking the custom_role_id at the user model
  // here however, the `getUserRole` returns "custom_role" if the id is present,
  // so we can check the role === "agent" directly
  if (['administrator', 'agent'].includes(role)) {
    return true;
  }

  // Check for full conversation management permission
  if (permissions.includes('conversation_manage')) {
    return true;
  }

  const conversationAssignee = conversation.meta.assignee;
  const isUnassigned = isConversationUnassigned(
    conversation,
    assignmentConsideration
  );
  const isAssignedToUser = conversationAssignee?.id === currentUserId;

  // Check unassigned management permission
  if (permissions.includes('conversation_unassigned_manage')) {
    return isUnassigned || isAssignedToUser;
  }

  // Check participating conversation management permission
  if (permissions.includes('conversation_participating_manage')) {
    return isAssignedToUser;
  }

  return false;
};

const SORT_OPTIONS = {
  last_activity_at_asc: ['sortOnLastActivityAt', 'asc'],
  last_activity_at_desc: ['sortOnLastActivityAt', 'desc'],
  created_at_asc: ['sortOnCreatedAt', 'asc'],
  created_at_desc: ['sortOnCreatedAt', 'desc'],
  priority_asc: ['sortOnPriority', 'asc'],
  priority_desc: ['sortOnPriority', 'desc'],
  waiting_since_asc: ['sortOnWaitingSince', 'asc'],
  waiting_since_desc: ['sortOnWaitingSince', 'desc'],
};
const sortAscending = (valueA, valueB) => valueA - valueB;
const sortDescending = (valueA, valueB) => valueB - valueA;

const getSortOrderFunction = sortOrder =>
  sortOrder === 'asc' ? sortAscending : sortDescending;

const sortConfig = {
  sortOnLastActivityAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.last_activity_at, b.last_activity_at),

  sortOnCreatedAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.created_at, b.created_at),

  sortOnPriority: (a, b, sortDirection) => {
    const DEFAULT_FOR_NULL = sortDirection === 'asc' ? 5 : 0;

    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;

    return getSortOrderFunction(sortDirection)(p1, p2);
  },

  sortOnWaitingSince: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    if (!a.waiting_since || !b.waiting_since) {
      if (!a.waiting_since && !b.waiting_since) {
        return sortFunc(a.created_at, b.created_at);
      }
      return sortFunc(a.waiting_since ? 0 : 1, b.waiting_since ? 0 : 1);
    }

    return sortFunc(a.waiting_since, b.waiting_since);
  },
};

// Pinned conversations always sort to the top, ordered by their pinned_at
// timestamp (newest pin first). Inside an "is-pinned" or "not-pinned" group,
// fall back to the user-selected sort key.
// SID: conversation-pinning.LIST_ORDER.1 conversation-pinning.LIST_ORDER.4
export const sortComparator = (a, b, sortKey) => {
  const aPinned = Boolean(a?.pinned);
  const bPinned = Boolean(b?.pinned);

  if (aPinned && !bPinned) return -1;
  if (!aPinned && bPinned) return 1;
  if (aPinned && bPinned) {
    const aTs = a.pinned_at ? new Date(a.pinned_at).getTime() : 0;
    const bTs = b.pinned_at ? new Date(b.pinned_at).getTime() : 0;
    if (aTs !== bTs) return bTs - aTs;
  }

  const [sortMethod, sortDirection] =
    SORT_OPTIONS[sortKey] || SORT_OPTIONS.last_activity_at_desc;
  return sortConfig[sortMethod](a, b, sortDirection);
};
